import { useCallback, useEffect, useRef, useState } from "react";

import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";

/**
 * Ocrolus book status for a loan (GET /files/ocrolus_book/{dealId}).
 *
 * Returns the raw status payload plus `loading` / `error` / `reload`, and the
 * derived `state` the UI branches on. Fetched here (not in the panel) so the
 * Documents-tab badge and the Ocrolus panel share one request.
 *
 * Derived states, per eastview-os frontend_documentation/ocrolus-book-and-notes.md:
 *   • "none"       — book_uuid == null → offer Create Book
 *   • "empty"      — book exists, nothing sent yet
 *   • "processing" — filesSent > 0 but accountsReady false (results arrive by
 *                    webhook, typically minutes — NOT an error)
 *   • "ready"      — accountsReady true
 *
 * Pass `enabled: false` to skip the fetch entirely.
 */
export const useOcrolusBook = (dealId, { enabled = true } = {}) => {
  const { getOcrolusBook } = useLoanFilesHook();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Monotonic request id so a stale in-flight response can't overwrite a newer
  // one (dealId change / rapid reloads after create+assign).
  const reqRef = useRef(0);

  const reload = useCallback(() => {
    if (!enabled || !dealId) return;
    const req = ++reqRef.current;
    setLoading(true);
    setError(null);
    getOcrolusBook({
      dealId,
      onSuccessFn: (data) => {
        if (req !== reqRef.current) return;
        setBook(data || null);
        setLoading(false);
      },
      onFailFn: (e) => {
        if (req !== reqRef.current) return;
        setError(e || true);
        setBook(null);
        setLoading(false);
      },
    });
    // getOcrolusBook is re-created every render by useLoanFilesHook (it isn't
    // memoized), so it is deliberately NOT a dependency — including it would
    // reload on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, enabled]);

  useEffect(() => {
    if (!enabled || !dealId) {
      setBook(null);
      return;
    }
    reload();
  }, [enabled, dealId, reload]);

  const hasBook = !!book?.book_uuid;
  const filesSent = book?.filesSent || 0;
  const state = !hasBook
    ? "none"
    : book?.accountsReady
      ? "ready"
      : filesSent > 0
        ? "processing"
        : "empty";

  return { book, state, hasBook, filesSent, loading, error, reload };
};

export default useOcrolusBook;
